import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, RefreshCw, Undo2, Paintbrush, MousePointer2, Plus, WandSparkles, X } from 'lucide-react';
import {
  getPlannerSeason, getPlannerFixtureChanges, acceptPlannerFixtureChange, rejectPlannerFixtureChange,
  syncPlannerFixtures, createPlannerFixture, updatePlannerFixture, deletePlannerFixture,
  setPlannerDay, setPlannerDays, fillPlannerDays,
} from '../../api';
import { useUrlParam } from '../../hooks/useUrlParam';
import {
  DOW, monthBounds, shiftMonth, monthTitle, dayNum, fmtDayShort, fmtRange, fmtSyncTime, fixtureLine, localToday,
} from './plannerShared';
import Overlay from './PlannerOverlay';

// Season view of the Club Planner: a month grid of game / training / off days with the server's
// "+n / −m" labels, the fixture-changes banner from the feed sync, and fast editing of the
// long-term day plan (paint mode, range fill, undo). Day types, labels and kickoff times all
// come from GET /api/planner/season — nothing is derived here.

const UNDO_LIMIT = 30;

export default function PlannerSeason({ token, onOpenWeek }) {
  const [month, setMonth] = useUrlParam('month', localToday().slice(0, 7));
  const bounds = useMemo(() => monthBounds(month), [month]);
  const [data, setData] = useState(null);
  const [changes, setChanges] = useState([]);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null); // { kind: 'ok' | 'err', text }
  const [mode, setMode] = useState('select'); // 'select' | 'training' | 'off'
  const [undoStack, setUndoStack] = useState([]);
  const [busy, setBusy] = useState(false);
  const [openDate, setOpenDate] = useState(null);
  const [fixtureDialog, setFixtureDialog] = useState(null); // { fixture } | { date }
  const [fillOpen, setFillOpen] = useState(false);
  const [stroke, setStroke] = useState(null);
  const strokeRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [season, pending] = await Promise.all([
        getPlannerSeason(token, bounds.gridFrom, bounds.gridTo),
        getPlannerFixtureChanges(token),
      ]);
      setData(season);
      setChanges(pending);
      setError(null);
    } catch (e) {
      setError(e.status === 401 ? 'session' : e.status === 403 ? 'access' : e.message);
    }
  }, [token, bounds.gridFrom, bounds.gridTo]);

  useEffect(() => { load(); }, [load]);

  const daysByDate = useMemo(() => Object.fromEntries((data?.days || []).map(d => [d.date, d])), [data]);
  const canEdit = !!data?.can_edit;

  const run = async (fn, okText) => {
    setBusy(true);
    try {
      const r = await fn();
      if (okText) setNotice({ kind: 'ok', text: typeof okText === 'function' ? okText(r) : okText });
      return r;
    } catch (e) {
      setNotice({ kind: 'err', text: e.message });
      return null;
    } finally {
      setBusy(false);
      load();
    }
  };

  // Day-type changes (paint, panel, undo) go through one path: optimistic local update, one
  // batch request, and the server's "previous" list becomes the undo entry.
  const applyDayChanges = useCallback(async (list, { undoable = true, message } = {}) => {
    if (!list.length) return;
    const byDate = Object.fromEntries(list.map(c => [c.date, c.day_type]));
    setData(prev => prev && {
      ...prev,
      days: prev.days.map(d => (byDate[d.date] && d.type !== 'game' ? { ...d, stored_day_type: byDate[d.date], type: byDate[d.date] } : d)),
    });
    setBusy(true);
    try {
      const r = await setPlannerDays(token, list);
      if (undoable) setUndoStack(s => [...s.slice(-(UNDO_LIMIT - 1)), r.previous]);
      if (message) setNotice({ kind: 'ok', text: message });
    } catch (e) {
      setNotice({ kind: 'err', text: e.message });
    } finally {
      setBusy(false);
      load();
    }
  }, [token, load]);

  const commitPaint = useCallback(dates => {
    if (mode === 'select') return;
    const list = dates
      .filter(date => daysByDate[date] && daysByDate[date].type !== 'game' && daysByDate[date].stored_day_type !== mode)
      .map(date => ({ date, day_type: mode }));
    applyDayChanges(list);
  }, [mode, daysByDate, applyDayChanges]);

  useEffect(() => {
    const end = () => {
      if (!strokeRef.current) return;
      const dates = [...strokeRef.current];
      strokeRef.current = null;
      setStroke(null);
      commitPaint(dates);
    };
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', end);
    return () => {
      window.removeEventListener('pointerup', end);
      window.removeEventListener('pointercancel', end);
    };
  }, [commitPaint]);

  const painting = canEdit && mode !== 'select';

  const onCellPointerDown = (date, e) => {
    if (!painting || e.button > 0) return;
    e.preventDefault();
    strokeRef.current = new Set([date]);
    setStroke(new Set(strokeRef.current));
  };

  // pointermove + elementFromPoint (not pointerenter) so a drag also paints on touch screens.
  const onGridPointerMove = e => {
    if (!strokeRef.current) return;
    const el = document.elementFromPoint(e.clientX, e.clientY);
    const date = el && el.closest ? el.closest('[data-date]')?.getAttribute('data-date') : null;
    if (date && !strokeRef.current.has(date)) {
      strokeRef.current.add(date);
      setStroke(new Set(strokeRef.current));
    }
  };

  const onCellClick = (date, e) => {
    if (!painting) { setOpenDate(date); return; }
    if (e.detail === 0) commitPaint([date]); // keyboard activation; pointer painting is handled above
  };

  const undo = () => {
    const last = undoStack[undoStack.length - 1];
    if (!last) return;
    setUndoStack(s => s.slice(0, -1));
    applyDayChanges(last, { undoable: false, message: `Undone (${last.length} day${last.length === 1 ? '' : 's'}).` });
  };

  const acceptChange = id => run(() => acceptPlannerFixtureChange(token, id), r => {
    const wk = (r?.shifted_weeks || []).map(w => w.iso_week);
    return wk.length ? `Accepted. Day labels shifted in week ${wk.join(', ')}.` : 'Accepted. No day labels changed.';
  });
  const rejectChange = id => run(() => rejectPlannerFixtureChange(token, id), 'Rejected. The stored fixture stays as it is.');
  const syncNow = () => run(() => syncPlannerFixtures(token), r => (
    r?.firstImport ? `Imported ${r.imported} fixtures from the feed.` : `Sync done: ${r?.changesCreated || 0} new change(s) found.`
  ));

  if (error === 'session') return <Message>Your session has expired. Sign out and sign in again to use the Planner.</Message>;
  if (error === 'access') return <Message>You don't have access to the Planner. Ask an admin for the "Planner" tab.</Message>;
  if (error && !data) return <Message>Could not load the planner: {error}</Message>;
  if (!data) return <Message>Loading…</Message>;

  const openDay = openDate ? daysByDate[openDate] : null;
  const lastSync = data.last_sync;

  return (
    <div>
      {changes.length > 0 && (
        <FixtureChangesBanner changes={changes} canEdit={canEdit} busy={busy} onAccept={acceptChange} onReject={rejectChange} />
      )}

      {/* Toolbar */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <button className="pl-btn" aria-label="Previous month" onClick={() => setMonth(shiftMonth(month, -1))}><ChevronLeft size={16} /></button>
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, minWidth: 150, textAlign: 'center' }}>{monthTitle(month)}</h3>
        <button className="pl-btn" aria-label="Next month" onClick={() => setMonth(shiftMonth(month, 1))}><ChevronRight size={16} /></button>
        <button className="pl-btn" onClick={() => setMonth(localToday().slice(0, 7))}>Today</button>
        <div style={{ flex: 1 }} />
        {canEdit && (
          <>
            <div role="group" aria-label="Edit mode" style={{ display: 'flex', gap: 4 }}>
              <button className={`pl-btn ${mode === 'select' ? 'pl-btn--active' : ''}`} onClick={() => setMode('select')} title="Click a day to open it"><MousePointer2 size={14} /> Select</button>
              <button className={`pl-btn ${mode === 'training' ? 'pl-btn--active' : ''}`} onClick={() => setMode('training')} title="Click or drag across days to make them training days"><Paintbrush size={14} /> Training</button>
              <button className={`pl-btn ${mode === 'off' ? 'pl-btn--active' : ''}`} onClick={() => setMode('off')} title="Click or drag across days to make them off days"><Paintbrush size={14} /> Off</button>
            </div>
            <button className="pl-btn" onClick={undo} disabled={!undoStack.length || busy}><Undo2 size={14} /> Undo</button>
            <button className="pl-btn" onClick={() => setFillOpen(true)}><WandSparkles size={14} /> Fill range…</button>
            <button className="pl-btn" onClick={() => setFixtureDialog({ date: bounds.first })}><Plus size={14} /> Fixture</button>
            <button className="pl-btn" onClick={syncNow} disabled={busy}><RefreshCw size={14} /> Sync now</button>
          </>
        )}
      </div>

      {/* Sync status + legend */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center', fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>
        <Legend />
        <span>“+2 / −4” = 2 days after the last game, 4 before the next.</span>
        <div style={{ flex: 1 }} />
        {!lastSync && <span>Fixtures not synced yet{canEdit ? ' — press Sync now to import the season.' : '.'}</span>}
        {lastSync && lastSync.ok && <span>Fixture feed synced {fmtSyncTime(lastSync.ran_at)}</span>}
        {lastSync && !lastSync.ok && (
          <span style={{ color: 'var(--pl-err)' }} title={lastSync.error || ''}>
            Last sync failed ({fmtSyncTime(lastSync.ran_at)}){data.last_successful_sync_at ? ` · last good sync ${fmtSyncTime(data.last_successful_sync_at)}` : ''}
          </span>
        )}
      </div>

      {notice && (
        <div role="status" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', marginBottom: 10, borderRadius: 8, fontSize: 13, border: `1px solid ${notice.kind === 'err' ? 'var(--pl-err)' : 'var(--pl-ok)'}`, color: 'var(--text-primary)' }}>
          <span style={{ flex: 1 }}>{notice.text}</span>
          <button className="pl-btn" aria-label="Dismiss" onClick={() => setNotice(null)} style={{ minHeight: 26, padding: '2px 6px' }}><X size={13} /></button>
        </div>
      )}

      {painting && (
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>
          Painting <strong>{mode === 'off' ? 'off days' : 'training days'}</strong>: click or drag across days. Game days are left alone. Switch back to Select to open a day.
        </div>
      )}

      {/* Month grid: one row per ISO week */}
      <div style={{ overflowX: 'auto' }}>
        <div
          onPointerMove={onGridPointerMove}
          style={{ display: 'grid', gridTemplateColumns: '118px repeat(7, minmax(98px, 1fr))', gap: 6, minWidth: 880, touchAction: painting ? 'none' : 'auto' }}
        >
          <div />
          {DOW.map(d => <div key={d} style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', padding: '0 4px' }}>{d}</div>)}
          {data.weeks.map(w => (
            <React.Fragment key={`${w.iso_year}-${w.iso_week}`}>
              <WeekHeader week={w} onOpen={() => onOpenWeek(w.iso_year, w.iso_week)} />
              {Array.from({ length: 7 }, (_, i) => {
                const date = addDaysLocal(w.monday, i);
                const day = daysByDate[date];
                if (!day) return <div key={date} />;
                const previewType = stroke && stroke.has(date) && day.type !== 'game' ? mode : null;
                return (
                  <DayCell
                    key={date}
                    day={day}
                    previewType={previewType}
                    outside={date < bounds.first || date > bounds.last}
                    isToday={date === data.today}
                    onPointerDown={e => onCellPointerDown(date, e)}
                    onClick={e => onCellClick(date, e)}
                  />
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>

      {openDay && (
        <DayPanel
          day={openDay}
          canEdit={canEdit}
          busy={busy}
          onClose={() => setOpenDate(null)}
          onSetType={type => applyDayChanges([{ date: openDay.date, day_type: type }])}
          onSaveNote={note => run(() => setPlannerDay(token, openDay.date, { day_type: openDay.stored_day_type, note }), 'Note saved.')}
          onToggleCounts={f => run(() => updatePlannerFixture(token, f.id, f.source === 'manual'
            ? { ...manualBody(f), counts_for_labels: !f.counts_for_labels }
            : { counts_for_labels: !f.counts_for_labels }), 'Fixture updated. Day labels recalculated.')}
          onEditFixture={f => setFixtureDialog({ fixture: f })}
          onDeleteFixture={f => {
            if (!window.confirm(`Delete ${fixtureLine(f)} on ${fmtDayShort(f.match_date)}?`)) return;
            run(() => deletePlannerFixture(token, f.id), 'Fixture deleted.');
          }}
          onAddFixture={() => setFixtureDialog({ date: openDay.date })}
        />
      )}

      {fixtureDialog && (
        <FixtureDialog
          initial={fixtureDialog}
          busy={busy}
          onClose={() => setFixtureDialog(null)}
          onSave={async body => {
            const r = await run(
              () => (fixtureDialog.fixture ? updatePlannerFixture(token, fixtureDialog.fixture.id, body) : createPlannerFixture(token, body)),
              fixtureDialog.fixture ? 'Fixture updated.' : 'Fixture added.'
            );
            if (r) setFixtureDialog(null);
          }}
        />
      )}

      {fillOpen && (
        <FillDialog
          defaults={{ from: bounds.first, to: bounds.last }}
          busy={busy}
          onClose={() => setFillOpen(false)}
          onFill={async body => {
            const r = await run(() => fillPlannerDays(token, body), x => `${x.changed} day${x.changed === 1 ? '' : 's'} updated.`);
            if (r) {
              if (r.previous.length) setUndoStack(s => [...s.slice(-(UNDO_LIMIT - 1)), r.previous]);
              setFillOpen(false);
            }
          }}
        />
      )}
    </div>
  );
}

function addDaysLocal(dateStr, n) {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}

function manualBody(f) {
  return {
    match_date: f.match_date, kickoff_time: f.kickoff_time || null, opponent: f.opponent, home_away: f.home_away,
    competition: f.competition, location: f.location, counts_for_labels: f.counts_for_labels,
  };
}

function Message({ children }) {
  return <div style={{ padding: '48px 16px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 14 }}>{children}</div>;
}

function Legend() {
  const sw = cls => <span className={`pl-cell ${cls}`} style={{ display: 'inline-block', width: 14, height: 14, minHeight: 0, padding: 0, borderRadius: 3, cursor: 'default' }} />;
  return (
    <span style={{ display: 'inline-flex', gap: 12, alignItems: 'center' }}>
      <span style={{ display: 'inline-flex', gap: 5, alignItems: 'center' }}>{sw('pl-cell--game')} Game day</span>
      <span style={{ display: 'inline-flex', gap: 5, alignItems: 'center' }}><span className="pl-tag pl-tag--tr" style={{ marginTop: 0 }}>TR</span> Training</span>
      <span style={{ display: 'inline-flex', gap: 5, alignItems: 'center' }}><span className="pl-tag pl-tag--off" style={{ marginTop: 0 }}>OFF</span> Off</span>
    </span>
  );
}

const WEEK_STATUS = { published: 'Published', draft: 'Draft' };

function WeekHeader({ week, onOpen }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3, padding: '6px 4px', fontSize: 12, color: 'var(--text-secondary)' }}>
      <span style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: 13 }}>Week {week.iso_week}</span>
      <span>{fmtRange(week.monday, week.sunday)}</span>
      <span style={{ color: week.status === 'published' ? 'var(--pl-ok)' : 'var(--text-muted)' }}>{WEEK_STATUS[week.status] || 'Not planned'}</span>
      <button className="pl-btn" onClick={onOpen} style={{ minHeight: 26, padding: '2px 8px', fontSize: 11, alignSelf: 'flex-start' }}>Open week</button>
    </div>
  );
}

function DayCell({ day, previewType, outside, isToday, onPointerDown, onClick }) {
  const type = previewType || day.type;
  const cls = ['pl-cell', type === 'game' ? 'pl-cell--game' : type === 'off' ? 'pl-cell--off' : '', outside ? 'pl-cell--outside' : '', isToday ? 'pl-cell--today' : '', previewType ? 'pl-cell--preview' : '']
    .filter(Boolean).join(' ');
  const desc = [fmtDayShort(day.date), day.label.text, type === 'off' ? 'off' : type === 'game' ? 'game day' : 'training',
    ...day.fixtures.map(f => `${fixtureLine(f)} ${f.kickoff_time || 'kickoff to be confirmed'}`)].filter(Boolean).join(', ');
  return (
    <button type="button" data-date={day.date} className={cls} onPointerDown={onPointerDown} onClick={onClick} aria-label={desc}>
      <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 4 }}>
        <span style={{ fontWeight: 700, fontSize: 13 }}>{dayNum(day.date)}</span>
        {day.label.text && <span className="pl-pill">{day.label.text}</span>}
      </span>
      {day.fixtures.map(f => (
        <span key={f.id} style={{ fontSize: 12, fontWeight: 600, lineHeight: 1.25 }}>
          {fixtureLine(f)}
          <span style={{ display: 'block', fontWeight: 400, color: 'var(--pl-game-sub)' }}>
            {f.kickoff_time || 'KO TBC'}{!f.counts_for_labels ? ' · not counted' : ''}
          </span>
        </span>
      ))}
      {day.template_hint && type === 'training' && (
        <span title={`From the scenario ${day.scenario?.code} template`} style={{ fontSize: 11, fontStyle: 'italic', opacity: 0.8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{day.template_hint}</span>
      )}
      {day.note && (
        <span title={day.note} style={{ fontSize: 11, opacity: 0.85, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{day.note}</span>
      )}
      {type === 'training' && <span className="pl-tag pl-tag--tr" aria-hidden="true">TR</span>}
      {type === 'off' && <span className="pl-tag pl-tag--off" aria-hidden="true">OFF</span>}
    </button>
  );
}

function FixtureChangesBanner({ changes, canEdit, busy, onAccept, onReject }) {
  return (
    <div style={{ border: '1px solid var(--pl-warn-border)', background: 'var(--pl-warn-bg)', borderRadius: 10, padding: '10px 14px', marginBottom: 14 }}>
      <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>
        Fixture sync · {changes.length} change{changes.length === 1 ? '' : 's'} found
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>
        Nothing changes until you accept. Accepting shifts the +/− day labels around the game{canEdit ? '' : ' (planner editors can accept or reject)'}.
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {changes.map(c => (
          <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', fontSize: 13 }}>
            <span style={{ flex: 1, minWidth: 240 }}>{c.description}</span>
            {canEdit && (
              <>
                <button className="pl-btn pl-btn--primary" disabled={busy} onClick={() => onAccept(c.id)}>Accept</button>
                <button className="pl-btn" disabled={busy} onClick={() => onReject(c.id)}>Reject</button>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function DayPanel({ day, canEdit, busy, onClose, onSetType, onSaveNote, onToggleCounts, onEditFixture, onDeleteFixture, onAddFixture }) {
  const [note, setNote] = useState(day.note || '');
  useEffect(() => { setNote(day.note || ''); }, [day.date, day.note]);
  return (
    <Overlay title={fmtDayShort(day.date)} onClose={onClose}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        {day.label.text && <span className="pl-pill">{day.label.text}</span>}
        <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{day.type === 'game' ? 'Game day' : day.type === 'off' ? 'Off day' : 'Training day'}</span>
      </div>

      {day.type !== 'game' && canEdit && (
        <div role="group" aria-label="Day type" style={{ display: 'flex', gap: 6 }}>
          <button className={`pl-btn ${day.stored_day_type === 'training' ? 'pl-btn--active' : ''}`} disabled={busy} onClick={() => onSetType('training')}>Training</button>
          <button className={`pl-btn ${day.stored_day_type === 'off' ? 'pl-btn--active' : ''}`} disabled={busy} onClick={() => onSetType('off')}>Off</button>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)' }}>Fixtures</div>
        {!day.fixtures.length && <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>No fixture on this day.</div>}
        {day.fixtures.map(f => (
          <div key={f.id} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 10, display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13 }}>
            <div style={{ fontWeight: 600 }}>{fixtureLine(f)}</div>
            <div style={{ color: 'var(--text-secondary)' }}>
              Kickoff {f.kickoff_time || 'to be confirmed'}{f.location ? ` · ${f.location}` : ''}
            </div>
            <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>{f.source === 'ics' ? 'From the fixture feed' : 'Added by hand'}</div>
            {canEdit && (
              <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
                <input type="checkbox" checked={f.counts_for_labels} disabled={busy} onChange={() => onToggleCounts(f)} />
                Counts for the +/− day labels
              </label>
            )}
            {canEdit && f.source === 'manual' && (
              <div style={{ display: 'flex', gap: 6 }}>
                <button className="pl-btn" onClick={() => onEditFixture(f)}>Edit</button>
                <button className="pl-btn" onClick={() => onDeleteFixture(f)}>Delete</button>
              </div>
            )}
          </div>
        ))}
        {canEdit && <button className="pl-btn" style={{ alignSelf: 'flex-start' }} onClick={onAddFixture}><Plus size={14} /> Add fixture on this day</button>}
      </div>

      <label className="pl-label">
        Note (staff)
        <textarea className="pl-input" rows={3} value={note} disabled={!canEdit} onChange={e => setNote(e.target.value)} placeholder="e.g. Training camp, national team break" />
      </label>
      {canEdit && (
        <button className="pl-btn pl-btn--primary" style={{ alignSelf: 'flex-start' }} disabled={busy || note === (day.note || '')} onClick={() => onSaveNote(note.trim())}>Save note</button>
      )}
    </Overlay>
  );
}

function FixtureDialog({ initial, busy, onClose, onSave }) {
  const f = initial.fixture;
  const [form, setForm] = useState({
    match_date: f?.match_date || initial.date || localToday(),
    kickoff_time: f?.kickoff_time || '',
    opponent: f?.opponent || '',
    home_away: f?.home_away || 'H',
    competition: f?.competition || 'Friendly',
    location: f?.location || '',
    counts_for_labels: f ? f.counts_for_labels : false,
  });
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const valid = form.match_date && form.opponent.trim();
  return (
    <Overlay title={f ? 'Edit fixture' : 'Add fixture'} onClose={onClose}>
      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
        For friendlies and games the feed doesn't have. League and cup games come in from the feed.
      </div>
      <label className="pl-label">Date<input className="pl-input" type="date" value={form.match_date} onChange={e => set('match_date', e.target.value)} /></label>
      <label className="pl-label">Kickoff (leave empty if not known yet)<input className="pl-input" type="time" value={form.kickoff_time} onChange={e => set('kickoff_time', e.target.value)} /></label>
      <label className="pl-label">Opponent<input className="pl-input" value={form.opponent} onChange={e => set('opponent', e.target.value)} /></label>
      <div role="group" aria-label="Home or away" style={{ display: 'flex', gap: 6 }}>
        <button type="button" className={`pl-btn ${form.home_away === 'H' ? 'pl-btn--active' : ''}`} onClick={() => set('home_away', 'H')}>Home</button>
        <button type="button" className={`pl-btn ${form.home_away === 'A' ? 'pl-btn--active' : ''}`} onClick={() => set('home_away', 'A')}>Away</button>
      </div>
      <label className="pl-label">Competition<input className="pl-input" value={form.competition} onChange={e => set('competition', e.target.value)} /></label>
      <label className="pl-label">Location<input className="pl-input" value={form.location} onChange={e => set('location', e.target.value)} /></label>
      <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
        <input type="checkbox" checked={form.counts_for_labels} onChange={e => set('counts_for_labels', e.target.checked)} />
        Counts for the +/− day labels (usually off for a friendly)
      </label>
      <button className="pl-btn pl-btn--primary" style={{ alignSelf: 'flex-start' }} disabled={busy || !valid}
        onClick={() => onSave({ ...form, kickoff_time: form.kickoff_time || null, opponent: form.opponent.trim(), location: form.location.trim() || null })}>
        {f ? 'Save fixture' : 'Add fixture'}
      </button>
    </Overlay>
  );
}

function FillDialog({ defaults, busy, onClose, onFill }) {
  const [from, setFrom] = useState(defaults.from);
  const [to, setTo] = useState(defaults.to);
  const [offAfter, setOffAfter] = useState([1]);
  const toggle = n => setOffAfter(prev => (prev.includes(n) ? prev.filter(x => x !== n) : [...prev, n].sort()));
  return (
    <Overlay title="Fill a date range" onClose={onClose}>
      <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
        Every day in the range becomes a training day, except the days you mark off below. Game days are left alone.
        Adjust single days afterwards with the Training / Off brushes. Undo reverts the whole fill.
      </div>
      <label className="pl-label">From<input className="pl-input" type="date" value={from} onChange={e => setFrom(e.target.value)} /></label>
      <label className="pl-label">To<input className="pl-input" type="date" value={to} onChange={e => setTo(e.target.value)} /></label>
      {[1, 2].map(n => (
        <label key={n} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
          <input type="checkbox" checked={offAfter.includes(n)} onChange={() => toggle(n)} />
          Day +{n} after every game is off
        </label>
      ))}
      <button className="pl-btn pl-btn--primary" style={{ alignSelf: 'flex-start' }} disabled={busy || !from || !to || from > to}
        onClick={() => onFill({ from, to, off_after_game: offAfter })}>
        Fill range
      </button>
    </Overlay>
  );
}
